import { Fragment, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Leaf } from "lucide-react";

import { Gradients } from "components/gradients";
import { useFuelStatistics } from "hooks/use-fuel-statistics";
import { useAnimatedValue } from "hooks/use-animated-value";
import { useGauge } from "hooks/use-gauge";
import { useStore } from "hooks/use-store";
import { PLACEHOLDER } from "utils/constants";
import { Color } from "utils/theme";
import { opacity } from "utils/canvas";
import { EcoRange } from "types/eco-sog";
import {
  DEFAULT_TICK_COLOR,
  MAIN_ARC_BG,
  MAIN_ARC_STROKE,
  OUTER_ARC_BG,
  OUTER_ARC_STROKE,
} from "components/gauge-style";
import { SvgGradients } from "types/enums";

// Constants
const START_ANGLE = 30;
const END_ANGLE = 330;
const DIAMETER = 450;
const ARC_WIDTH = 22;
const TICK_OFFSET = ARC_WIDTH + 10;
const TICK_WIDTH = 4;
const TICK_LENGTH = 15;
const STROKE_CAP = "round";
const FALLBACK_MAX_VALUE = 3000;
const MIN_RPM_TO_SHOW = 1;

export const EcoGaugeRpm = () => {
  const { t } = useTranslation();

  // Store values
  const rpm = useStore((state) => state.rpm);
  const ecoSog = useStore((state) => state.ecoSog);

  // Animated values for smooth transitions
  const animatedRpm = useAnimatedValue(rpm);

  // Fuel statistics
  const {
    fuelCurveStatusMessage,
    ecoSogRpm,
    isValueInEcoRangeRpm,
    isInEcoRangeRpm,
  } = useFuelStatistics();

  // Determine max RPM for gauge
  let maxRpm = ecoSog?.max_rpm ? ecoSog.max_rpm + 200 : FALLBACK_MAX_VALUE;
  maxRpm = Math.ceil(maxRpm / 500) * 500;

  // Gauge setup
  const gauge = useGauge({
    domain: [0, maxRpm],
    startAngle: START_ANGLE,
    endAngle: END_ANGLE,
    numTicks: maxRpm / 100,
    diameter: DIAMETER,
  });

  // Determine tick color based on current SOG
  const getTickColor = useCallback(
    (tickValue: number, defaultColor: string) => {
      const inRange = isValueInEcoRangeRpm(tickValue);
      const tickInRpmRange = rpm !== null && tickValue <= rpm;

      // In eco range and in SOG range
      if (inRange && tickInRpmRange) return Color.Emerald[400];

      // In eco range
      if (inRange) return opacity(Color.Emerald[400], 0.4);

      // In SOG range
      if (tickInRpmRange) return Color.Indigo[400];

      // Default tick color
      return defaultColor;
    },
    [rpm, isValueInEcoRangeRpm]
  );

  // Ensure minimum size for eco ranges
  const adjustRange = (range: EcoRange, minSizeRpm = 10) => {
    const size = range.end_rpm - range.start_rpm;

    if (size >= minSizeRpm) return range;

    const avg = (range.end_rpm + range.start_rpm) / 2;

    return {
      start_rpm: avg - minSizeRpm / 2,
      end_rpm: avg + minSizeRpm / 2,
    };
  };

  return (
    <svg
      {...gauge.getSVGProps(ARC_WIDTH * 1.5)}
      preserveAspectRatio="xMidYMid meet"
      className="w-full h-full"
    >
      {/* Defs */}
      <Gradients />

      {/* Main arc */}
      <g id="main-arc">
        <path
          id="main-arc-stroke"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 1.4}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-bg"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={MAIN_ARC_BG}
          strokeWidth={ARC_WIDTH * 1.3}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="main-arc-value"
          {...gauge.getArcProps({
            offset: 0,
            startAngle: START_ANGLE,
            endAngle:
              animatedRpm > maxRpm
                ? gauge.valueToAngle(maxRpm)
                : gauge.valueToAngle(
                    animatedRpm > MIN_RPM_TO_SHOW ? animatedRpm : 0
                  ),
          })}
          fill="none"
          stroke={
            isInEcoRangeRpm
              ? `url(#${SvgGradients.Green})`
              : `url(#${SvgGradients.Blue})`
          }
          strokeWidth={animatedRpm > 0 ? ARC_WIDTH : 0}
          strokeLinecap={STROKE_CAP}
        />
      </g>

      {/* Outer arc */}
      <g id="outer-arc">
        <path
          id="outer-arc-stroke"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_STROKE}
          strokeWidth={ARC_WIDTH * 0.55}
          strokeLinecap={STROKE_CAP}
        />
        <path
          id="outer-arc-bg"
          {...gauge.getArcProps({
            offset: ARC_WIDTH * 1.2,
            startAngle: START_ANGLE,
            endAngle: END_ANGLE,
          })}
          fill="none"
          stroke={OUTER_ARC_BG}
          strokeWidth={ARC_WIDTH * 0.5}
          strokeLinecap={STROKE_CAP}
        />
        {ecoSog?.eco_ranges?.map((range, index) => {
          return (
            <path
              key={`outer-arc-value-1-${index}`}
              id={`outer-arc-value-1-${index}`}
              {...gauge.getArcProps({
                offset: ARC_WIDTH * 1.2,
                startAngle: gauge.valueToAngle(adjustRange(range).start_rpm),
                endAngle: gauge.valueToAngle(adjustRange(range).end_rpm),
              })}
              fill="none"
              stroke={`url(#${SvgGradients.Green})`}
              opacity={isInEcoRangeRpm ? 1 : 0.5}
              strokeWidth={ARC_WIDTH * 0.5}
              strokeLinecap={STROKE_CAP}
            />
          );
        })}
      </g>

      {/* Ticks */}
      <g id="gauge-ticks">
        {gauge.ticks.map((angle, index) => {
          const asValue = Math.round(gauge.angleToValue(angle));
          const majorTickInterval = maxRpm <= 2000 ? 100 : 500;
          const showText = asValue % majorTickInterval === 0;
          const tickColor = getTickColor(asValue, DEFAULT_TICK_COLOR);

          return (
            <Fragment key={`tick-${angle}-${index}`}>
              <line
                strokeWidth={TICK_WIDTH}
                stroke={tickColor}
                strokeLinecap={"round"}
                {...gauge.getTickProps({
                  angle,
                  length: showText ? TICK_LENGTH : TICK_LENGTH * 0.5,
                  offset: -TICK_OFFSET,
                })}
              />
              {showText && (
                <text
                  className="text-base font-semibold font-mono"
                  fill={tickColor}
                  {...gauge.getLabelProps({ angle, offset: TICK_OFFSET * 2 })}
                >
                  {asValue}
                </text>
              )}
            </Fragment>
          );
        })}
      </g>

      {/* Center top label  */}
      <g id="center-top">
        {isInEcoRangeRpm && (
          <>
            <text
              y={"-20%"}
              x={5}
              className="text-xl font-semibold"
              fill={Color.Green[400]}
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              eco
            </text>
            <Leaf
              y={"-23%"}
              x={-38}
              alignmentBaseline="middle"
              dominantBaseline="middle"
              textAnchor="middle"
              className="inline-block"
              width={20}
              height={20}
              stroke={Color.Green[400]}
            />
          </>
        )}
      </g>

      {/* Center value and label */}
      <g id="center">
        <text
          y={"15%"}
          className="text-xl font-semibold uppercase"
          fill={isInEcoRangeRpm ? Color.Green[400] : Color.Primary[500]}
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {t("dashboard.rpm")}
        </text>
        <text
          fill={isInEcoRangeRpm ? Color.Green[400] : Color.White}
          className="text-7xl font-bold font-mono"
          alignmentBaseline="middle"
          dominantBaseline="middle"
          textAnchor="middle"
        >
          {rpm !== null ? Math.round(rpm) : PLACEHOLDER}
        </text>
      </g>

      {/* Center bottom label and value */}
      <g id="center-bottom">
        {fuelCurveStatusMessage ? (
          <foreignObject
            x={"-19%"}
            y={"28%"}
            className="text-base font-medium text-center p-2"
            color={Color.Primary[400]}
            width={200}
            height={100}
          >
            <p>{fuelCurveStatusMessage}</p>
          </foreignObject>
        ) : (
          <>
            <text
              y={"45%"}
              className="text-lg font-semibold"
              fill={isInEcoRangeRpm ? Color.Green[400] : Color.Primary[500]}
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              {t("fuel_curve.eco_rpm")}
            </text>
            <text
              y={"38%"}
              fill={isInEcoRangeRpm ? Color.Green[400] : Color.White}
              className="text-6xl font-semibold font-mono"
              alignmentBaseline="baseline"
              dominantBaseline="baseline"
              textAnchor="middle"
            >
              {ecoSogRpm !== null
                ? Math.round(ecoSogRpm / 100) * 100
                : PLACEHOLDER}
            </text>
          </>
        )}
      </g>
    </svg>
  );
};
